import React from 'react';
import { Loader2, Clock } from 'lucide-react';
import { organisateurApi, PortailInfo, ConviveOrga } from '../../../lib/organisateurApi';
import { useConvivesActions, ConvivesApi } from '../../Evenementiel/convives/useConvivesActions';
import type { Guest } from '../../Evenementiel/convives/guestFilters';
import { ConvivesImportModal } from '../../Evenementiel/convives/ConvivesImportModal';
import { useConfirm } from '../../ui/ConfirmProvider';
import { TuileChiffre } from '../../ui/TuileChiffre';
import { PagePublique, LogoEtablissement, EtatFinal } from '../patron/PagePublique';
import { OrganisateurGestes, fmtJour } from './OrganisateurGestes';
import { OrganisateurListe } from './OrganisateurListe';
import { OrganisateurEnvoiModal, OrganisateurRetraitModal } from './OrganisateurModales';
import { OrganisateurCartes } from './OrganisateurCartes';

const joursAvant = (d: string): number => Math.ceil((new Date(`${d.slice(0, 10)}T23:59:59`).getTime() - Date.now()) / 86400000);

/**
 * 19b — le portail. En-tête du dossier, bandeau de clôture, quatre compteurs, les trois gestes, la liste,
 * les contraintes en nombre et le mot d'accueil. Les gestes passent par `useConvivesActions` — le même
 * hook que l'établissement (lots de 25, relance) — branché sur l'API du portail : une seule logique.
 */
export function OrganisateurPortail({ token, onSessionPerdue, onDeconnexion }: {
    token: string;
    onSessionPerdue: (message: string) => void;
    onDeconnexion: () => void;
}) {
    const confirmAction = useConfirm();
    const [info, setInfo] = React.useState<PortailInfo | null>(null);
    const [modale, setModale] = React.useState<null | 'import' | 'envoi' | 'relance'>(null);
    const [retrait, setRetrait] = React.useState<ConviveOrga | null>(null);
    const [occupe, setOccupe] = React.useState(false);
    const [message, setMessage] = React.useState('');
    const fichierRef = React.useRef('');

    // Une réponse « session expirée » ou « accès expiré / révoqué » renvoie à la porte, qui dira pourquoi.
    const surRefus = React.useCallback((r: { status: number; data: { etat?: string; error?: string } }) => {
        if (r.status === 401) { onSessionPerdue('Votre session de 12 h a expiré : saisissez à nouveau votre code.'); return true; }
        if (r.status === 403) { onSessionPerdue(''); return true; }
        return false;
    }, [onSessionPerdue]);

    const api = React.useMemo<ConvivesApi>(() => ({
        list: async () => {
            const r = await organisateurApi.portail(token);
            if (!r.ok) { if (!surRefus(r)) throw new Error(r.data.error || 'Erreur'); return []; }
            setInfo(r.data);
            return r.data.convives as unknown as Guest[];
        },
        import: async (rows) => {
            const r = await organisateurApi.importer(token, rows, fichierRef.current);
            if (!r.ok) { surRefus(r); throw new Error(r.data.error || (r.data.etat === 'clos' ? 'La collecte est fermée.' : "Erreur lors de l'import.")); }
            return r.data;
        },
        remove: async (guestId) => {
            const r = await organisateurApi.retirer(token, guestId);
            if (!r.ok) { surRefus(r); throw new Error(r.data.error || 'Ce convive ne peut pas être retiré.'); }
            return r.data;
        },
        send: async (guestIds, mode) => {
            const r = await organisateurApi.envoyer(token, guestIds, mode === 'relance' ? 'relance' : 'envoi');
            if (!r.ok) { surRefus(r); throw new Error(r.data.error || "Erreur lors de l'envoi."); }
            return r.data;
        },
    }), [token, surRefus]);
    const actions = useConvivesActions(token, api);

    if (!info) return <div className="min-h-screen bg-surface-app flex items-center justify-center"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    const etab = info.etablissement || { name: '', logo_url: null, theme_color: null };
    const c = info.compteurs;
    const closDepuis = info.cloture.closed_at || info.cloture.locked_at;
    const closeDate = info.cloture.date || '';
    const jours = closeDate ? joursAvant(closeDate) : null;
    const nonInvites = actions.guests.filter((g) => !g.invitation_sent_at).map((g) => g.id);
    const silencieux = actions.guests.filter((g) => g.invitation_sent_at && !g.responded_at).map((g) => g.id);

    const retirer = async (g: ConviveOrga) => {
        if (g.responded_at) { setRetrait(g); return; }
        if (!(await confirmAction({ title: 'Retirer le convive ?', description: <><strong>{g.name}</strong> sera retiré de votre liste. Il n'a pas encore répondu.</>, confirmLabel: 'Retirer le convive' }))) return;
        await actions.removeGuest(g.id);
    };
    const signalerAbsence = async () => {
        if (!retrait) return;
        setOccupe(true);
        const r = await organisateurApi.signalerAbsence(token, retrait.id);
        setOccupe(false);
        setRetrait(null);
        setMessage(r.ok ? `Absence de ${retrait.name} signalée à ${etab.name}.` : (r.data.error || "L'absence n'a pas pu être signalée."));
        actions.reload();
    };

    return (
        <PagePublique etablissement={etab} largeur={880}>
            <div className="rounded-[14px] bg-surface-app border border-border overflow-hidden">
                {/* En-tête : le dossier, l'établissement, et qui est connecté */}
                <div className="flex items-center justify-between gap-3.5 flex-wrap px-5 py-3.5 bg-surface border-b border-border">
                    <div className="flex items-center gap-[11px] min-w-0">
                        <LogoEtablissement etablissement={etab} taille={34} />
                        <div className="min-w-0">
                            <div className="text-[13px] font-bold text-content truncate">{info.dossier?.label}</div>
                            <div className="text-[11.5px] text-content-muted">{etab.name} · {info.dossier?.date_label}</div>
                        </div>
                    </div>
                    <button type="button" onClick={onDeconnexion} className="inline-flex items-center gap-1.5 h-7 px-[11px] rounded-full bg-surface-soft border border-border text-content-muted text-[11.5px] font-semibold whitespace-nowrap hover:text-content hover:border-content transition-colors">
                        {info.organisateur?.name} · se déconnecter
                    </button>
                </div>

                {/* Bandeau de clôture — toujours une date en clair, jamais « bientôt » */}
                {closDepuis ? (
                    <div className="px-5 pt-4">
                        <EtatFinal ton="succes" icone="coche" titre="Réponses closes"
                            texte={<>La collecte est fermée depuis le {fmtJour(closDepuis)}. {c.reponses} réponse{c.reponses > 1 ? 's' : ''} sur {c.invites} {c.reponses > 1 ? 'ont' : 'a'} été transmise{c.reponses > 1 ? 's' : ''} à la cuisine.</>}
                            action="Le récapitulatif reste téléchargeable depuis la liste ci-dessous." />
                    </div>
                ) : (
                    <div className="flex items-center gap-3 px-5 py-[13px] bg-state-warn-soft border-b border-state-warn/30">
                        <span className="w-[30px] h-[30px] shrink-0 rounded-lg bg-surface border border-state-warn/30 text-state-warn flex items-center justify-center"><Clock size={15} /></span>
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-bold text-state-warn">{closeDate ? `Les réponses se ferment le ${fmtJour(closeDate)} à 23:59` : 'Aucune date de clôture réglée pour l’instant'}</div>
                            <div className="mt-px text-[12px] text-state-warn text-pretty">
                                {closeDate
                                    ? `${jours !== null && jours > 0 ? `Dans ${jours} jour${jours > 1 ? 's' : ''}. ` : jours === 0 ? "C'est aujourd'hui. " : ''}Passé cette date, plus aucun convive ne peut répondre et la cuisine part sur les réponses reçues.`
                                    : `${etab.name} la fixera ; elle sera annoncée dans chaque courriel envoyé à vos convives.`}
                            </div>
                        </div>
                    </div>
                )}

                <div className="px-5 py-4 flex flex-col gap-3.5">
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-2.5">
                        <TuileChiffre grande t={{ label: 'Convives importés', valeur: c.importes, ton: 'neutre' }} />
                        <TuileChiffre grande t={{ label: 'Invitations envoyées', valeur: c.invites, ton: 'info' }} />
                        <TuileChiffre grande t={{ label: 'Réponses reçues', valeur: c.reponses, unite: c.invites ? `sur ${c.invites}` : undefined, ton: 'succes' }} />
                        <TuileChiffre grande t={{ label: 'Sans réponse', valeur: c.silencieux, ton: 'attention' }} />
                    </div>

                    {info.plafond.restants <= 0 && !closDepuis && (
                        <EtatFinal ton="attention" icone="info" titre="Plafond du jour atteint" texte={<>{info.plafond.max} envois sont partis aujourd'hui pour ce dossier. Vous pourrez reprendre demain ; d'ici là, {etab.name} peut prendre le relais.</>} />
                    )}

                    {!closDepuis && (
                        <OrganisateurGestes info={info} occupe={actions.sending || actions.importing}
                            onImporter={() => setModale('import')} onEnvoyer={() => setModale('envoi')} onRelancer={() => setModale('relance')} />
                    )}

                    {(actions.error || message) && <p className="text-[12px] font-semibold text-content">{actions.error || message}</p>}
                    {actions.sendProgress && (
                        <p className="text-[12px] text-content-muted">
                            {actions.sendProgress.done}/{actions.sendProgress.total} traités — {actions.sendProgress.sent} envoyé(s){actions.sendProgress.failed > 0 ? `, ${actions.sendProgress.failed} échoué(s)` : ''}
                            {actions.sendProgress.failed > 0 && actions.sendProgress.lastError && <span className="block text-state-danger font-semibold mt-0.5">{actions.sendProgress.lastError}</span>}
                        </p>
                    )}

                    <OrganisateurListe convives={info.convives} nomDossier={info.dossier?.label || ''} occupe={actions.sending || !!closDepuis} onRetirer={retirer} />

                    <OrganisateurCartes token={token} info={info} onMotAccueil={(m) => setInfo({ ...info, mot_accueil: m })} />
                </div>
            </div>

            {modale === 'import' && (
                <ConvivesImportModal variante="organisateur" importing={actions.importing}
                    existants={info.convives.map((g) => ({ email: g.email, invite: !!g.invitation_sent_at }))}
                    onImport={(rows, nom) => { fichierRef.current = nom; return actions.importRows(rows); }}
                    onClose={() => setModale(null)} />
            )}
            {(modale === 'envoi' || modale === 'relance') && (
                <OrganisateurEnvoiModal info={info} mode={modale} nombre={modale === 'relance' ? silencieux.length : nonInvites.length} sending={actions.sending}
                    onConfirm={async () => { const m = modale; setModale(null); if (m === 'relance') await actions.remindUnresponded(); else await actions.sendAllInvitations(); }}
                    onClose={() => setModale(null)} />
            )}
            {retrait && <OrganisateurRetraitModal convive={retrait} occupe={occupe} onSignaler={signalerAbsence} onClose={() => setRetrait(null)} />}
        </PagePublique>
    );
}
